import styled from "@emotion/styled";
import { Button, Flex, Select, SelectProps, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { ReactNode, forwardRef, useState } from "react";

import { DEPARTMENT_QUERY_KEY, mutateDepartment } from "@/api/department/useDepartmentQuery";
import { DepartmentCreateForm } from "@/features/department/form/createForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { MasterApiDepartmentsPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  DepartmentOption,
  useDepartmentsAutoComplete,
  useDepartmentsCode,
} from "./auto-useGetDepartmentQuery";

interface DepartmentProps extends React.ComponentPropsWithoutRef<"div"> {
  group: string;
  label: string;
  value: string;
}

export interface DepartmentAutoCompleteProps
  extends Partial<SelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  value?: string | null;
  onChange?: (itemCode: string | null) => void;
  maxDropdownHeight?: number;
  width?: string;
}

export const DepartmentAutoComplete = (params: DepartmentAutoCompleteProps) => {
  const { value: DepartmentCode, onChange, maxDropdownHeight, width, ...etcParams } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  const { data: options } = useDepartmentsAutoComplete(focused, searchKeyword);

  const { data: initialOptions } = useDepartmentsCode(!!DepartmentCode, DepartmentCode ?? null);

  let selectedRoute = initialOptions?.find((item) => {
    return item.value === DepartmentCode;
  });

  const onChangeHandler = (e: string | null) => {
    selectedRoute = options?.find((item) => item.value === e);
    onChange && onChange(e);
  };

  const SelectItem = forwardRef<HTMLDivElement, DepartmentProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <Flex align={"center"}>
            <Text>{code}</Text>
            <Text fz="xs"> (name: {name === "null" ? "-" : name})</Text>
          </Flex>
        </Flex>
      </div>
    ),
  );

  return (
    <DepartmentSelect
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      clearable
      inputContainer={(children: ReactNode) => (
        <div style={{ position: "relative" }}>
          {children}
          {selectedRoute?.value && (
            <div
              style={{
                position: "absolute",
                bottom: "0px",
                fontSize: "0.8em",
                color: "#888",
                left: "10px",
              }}
            >
              {"code: " + selectedRoute?.value}
            </div>
          )}
        </div>
      )}
      value={DepartmentCode}
      itemComponent={SelectItem}
      data={[...(options ?? []), ...(initialOptions ?? [])].reduce(
        (unique: DepartmentProps[], option: DepartmentProps) => {
          return unique.some((u) => u.value === option.value) ? unique : [...unique, option];
        },
        [],
      )}
      searchable
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onChange={onChangeHandler}
      onSearchChange={setSearchKeyword}
      rightSection={DepartmentInfo({
        route: selectedRoute as DepartmentOption,
        onChange: onChangeHandler,
      })}
      filter={(value, item) =>
        item?.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item?.value?.toLowerCase().includes(value.toLowerCase().trim())
      }
      nothingFound={AddNewDepartment({
        DepartmentCode: searchKeyword,
        onChange: onChangeHandler,
      })}
      {...etcParams}
    />
  );
};

const DepartmentInfo = (params: {
  route?: DepartmentOption;
  onChange: (DepartmentCode: string | null) => void;
}) => {
  const { route, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  };

  return route?.value ? (
    <DepartmentInfoLabel>
      {/* ({route.value})  */}
      <IconX size="1rem" onClick={clearHandler} />
    </DepartmentInfoLabel>
  ) : null;
};

const AddNewDepartment = (params: {
  DepartmentCode: string;
  onChange: (itemCode: string) => void;
}) => {
  const { DepartmentCode, onChange } = params;
  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: postMutate } = useMutation(
    (params: MasterApiDepartmentsPostRequest) =>
      mutateDepartment.create(params).mutationFn(params as MasterApiDepartmentsPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([DEPARTMENT_QUERY_KEY]);
      },
    },
  );

  const addNewModalHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    openModal(
      <DepartmentCreateForm code={DepartmentCode} />,
      null,
      t("common.newCreate", { item: t("department.department") }),
      true,
    ).then((result) => {
      onChange(result.code);
    });
  };

  return (
    <Button variant="subtle" onClick={addNewModalHandler}>
      {t("common.newCreate", { item: `${t("department.department")} ${DepartmentCode}` })}
    </Button>
  );
};
const DepartmentInfoLabel = styled.div`
  font-size: 12px;
  color: #666666;
  // padding-right: 8px;
  padding-left: 0.4rem;
  display: flex;
  align-items: center;
  position: absolute;
`;

const DepartmentSelect = styled(Select)<{ value: string | null | undefined }>`
  width: 100%;

  .mantine-Select-input {
    font-size: 0.8rem;
    padding-bottom: ${(props) => (props.value ? "0.8rem !important" : "")};

    &.item-dirty {
      color: cadetblue !important;
      border-color: cadetblue;
    }
  }
  .mantine-Select-error {
    font-size: 11px;
    color: red;
  }
`;
